<!--
  - Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
  - This is CONFIDENTIAL code. Use is subject to license terms.
  -->
<template>
  <div
    ref="coreSlot"
    class="modal fade"
    tabindex="-1"
    role="dialog"
    v-on:keyup.esc="closeOnEsc ? cancel() : {}"
    v-on="doCloseOnEnter ? { keydown: triggerCloseOnEnter } : {}"
  >
    <div class="modal-dialog" :class="sizeClass" role="document">
      <div class="modal-content">
        <div
          class="modal-header flex-nowrap"
          :class="headerClass ? headerClass : 'bg-secondary-light text-primary-dark'"
        >
          <h5 class="modal-title"><span v-html="title"></span></h5>
          <div class="ml-auto">
            <slot name="header"></slot>
            <button type="button" class="close ml-0" aria-label="Close" @click="cancel()">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>

        <div class="modal-body">
          <slot v-if="!processing"></slot>
          <slot name="process-indicator" v-else>
            <div class="d-flex">
              <icon :icon="['fas', 'spinner']" size="3x" pulse class="mt-4 mb-4 spinning mx-auto"></icon>
            </div>
          </slot>
        </div>

        <div class="modal-footer" v-show="footerEnabled">
          <button v-if="!okOnly" type="button" class="btn btn-secondary" @click="cancel()">
            {{ cancelText || $t("confirms.cancel") }}
          </button>
          <slot name="additional-buttons"></slot>
          <button v-if="!cancelOnly" type="button" class="btn" :class="okClass" @click="ok()" :disabled="okDisabled">
            {{ okText || $t("confirms.ok") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Modal",

  props: {
    value: { type: Boolean, default: false }, // Default show/hide value
    title: "",
    size: "",
    headerClass: "",
    footerEnabled: { type: Boolean, default: true },
    focusId: null,
    okText: "",
    okClass: "",
    okOnly: false,
    okDisabled: false,
    cancelOnly: false,
    cancelText: "",
    dataBackdrop: "",
    processing: { type: Boolean, default: false },
    closeOnEsc: { type: Boolean, default: true },
    closeOnEnter: { type: Boolean, default: true },
  },

  data: () => ({
    type: "",
  }),

  computed: {
    sizeClass() {
      if (this.size) {
        return "modal-" + this.size
      }
      return ""
    },

    doCloseOnEnter() {
      // Triggers the set up for the enter on close logic; while we're at it
      // set the focus of the top level element.
      this.$nextTick(() => this.$refs.coreSlot.focus())
      return this.footerEnabled && this.closeOnEnter
    },
  },

  mounted() {
    this.$$(this.$el).on("hidden.bs.modal", this.fireHiddenEvent)
    this.$$(this.$el).on("hide.bs.modal", this.fireHideEvent)
    this.$$(this.$el).on("show.bs.modal", this.fireShowEvent)
    this.$$(this.$el).on("shown.bs.modal", this.fireShownEvent)
    this.$nextTick(function () {
      // TODO: Resolve a cleaner way to trigger the bootstrap modal to clean up
      // after itself during a back button
      this.$$(".modal-backdrop").remove()
      this.$$("body").removeClass("modal-open")
    })
  },

  watch: {
    value: function (val) {
      if (val) this.$$(this.$el).modal("show")
      else this.$$(this.$el).modal("hide")
    },
  },

  methods: {
    cancel() {
      this.type = "cancel"
      this.$$(this.$el).modal("hide")
    },

    ok() {
      this.type = "ok"
      this.$$(this.$el).modal("hide")
    },

    triggerCloseOnEnter(event) {
      if (event.keyCode === 13) {
        this.ok()
      }
    },
    fireHiddenEvent(e) {
      // Emit the value has changed to allow two-way binding
      this.$emit("input", false)
      this.$emit("hidden", e)
    },
    fireHideEvent(e) {
      // Inform parent why a request to hide has occurred. If there is no reason simply
      // let the parent know a hide occurred.
      // In all cases pass along the event to allow the parent to cancel.
      if (this.type === "ok") this.$emit("ok", e)
      else if (this.type === "cancel") this.$emit("cancel", e)
      else this.$emit("hide", e)

      // Reset type back to default
      this.type = ""
    },
    fireShowEvent(e) {
      this.$$(this.$el).data("bs.modal")._config.keyboard = this.closeOnEsc
      if (this.dataBackdrop && this.dataBackdrop !== "") {
        this.$$(this.$el).data("bs.modal")._config.backdrop = this.dataBackdrop
      }
      this.$emit("show", e)
    },
    fireShownEvent() {
      // if focusId is specified, fire auto-focus on show
      let focusField = document.getElementById(this.focusId)
      if (focusField) {
        focusField.focus()
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/assets/scss_custom/custom-variables.scss";

.modal-wide {
  max-width: 95vw !important;
}
</style>
